import { useState, useMemo, useCallback, useEffect, useRef, useDeferredValue } from "react";
import { useMap } from "@vis.gl/react-google-maps";
import { useSearch, useNavigate } from "@tanstack/react-router";
import { trpc } from "@/lib/trpc";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { GoogleMapWrapper } from "@/components/map/GoogleMapWrapper";
import { MapMarker } from "@/components/map/MapMarker";
import { MapFilters, type MapFilterState } from "@/components/map/MapFilters";
import { MapLegend } from "@/components/map/MapLegend";
import { LocationSidebar } from "@/components/map/LocationSidebar";
import { LocationTableDrawer } from "@/components/map/LocationTableDrawer";
import { MapTooltip } from "@/components/map/MapTooltip";
import { useScan } from "@/contexts/ScanContext";
import { useClusteredMarkers } from "@/hooks/useClusteredMarkers";
import { useMapTooltip } from "@/hooks/useMapTooltip";
import { useCourtPlacementMode } from "@/hooks/useCourtPlacementMode";
import { usePinEditMode } from "@/hooks/usePinEditMode";
import { SportCourtMarker } from "@/components/map/SportCourtMarker";
import { CourtPlacementToolbar } from "@/components/map/CourtPlacementToolbar";
import { ScanProgressPanel } from "@/components/scan-progress/ScanProgressPanel";
import { PolygonScanTool } from "@/components/map/PolygonScanTool";
import { ScanCircleOverlay } from "@/components/map/ScanCircleOverlay";
import { CollectionModePanel } from "@/components/map/CollectionModeButton";
import { useMapViewportBounds } from "@/hooks/useMapViewportBounds";
import { useCollectionMode } from "@/hooks/useCollectionMode";
import { AddToCollectionDialog } from "@/components/collections/AddToCollectionDialog";
import { useMapData } from "@/hooks/useMapData";
import { useSearchContext } from "@/contexts/SearchContext";
import { isLocalDev } from "@/lib/is-local-dev";

const NETHERLANDS_CENTER = { lat: 52.1326, lng: 5.2913 };
const DEFAULT_ZOOM = 7;

/** Get the pixel offset to shift the map center left to account for the sidebar. */
function getSidebarPanOffset(): number {
  const sidebarEl = document.querySelector<HTMLElement>("[data-location-sidebar]");
  if (sidebarEl) return sidebarEl.offsetWidth / 2;
  // Sidebar not yet rendered — use responsive fallback matching LocationSidebar widths
  const w = window.innerWidth;
  if (w >= 1536) return 280; // 2xl: w-[560px] / 2
  if (w >= 1280) return 240; // xl: w-[480px] / 2
  if (w >= 1024) return 220; // lg: w-[440px] / 2
  return 280;
}

function MapContent() {
  const map = useMap();
  const viewportBounds = useMapViewportBounds();
  const search = useSearch({ from: "/dashboard/" });
  const navigate = useNavigate();

  const filters: MapFilterState = {
    classification: search.classification ?? "all",
    ownership: search.ownership ?? "all",
    reviewStatus: search.reviewStatus ?? "all",
    source: search.source ?? "all",
    collectionIds: search.collectionIds ?? [],
    enableClustering: search.enableClustering ?? false,
    sportTypes: search.sportTypes ?? [],
    locationTypes: search.locationTypes ?? [],
    showCourtPins: search.showCourtPins ?? true,
  };

  const setFilters = useCallback((newFilters: MapFilterState) => {
    navigate({
      to: "/dashboard",
      search: (prev: Record<string, unknown>) => ({
        ...prev,
        ...newFilters,
      }),
      replace: true,
    });
  }, [navigate]);

  const [selectedLocationId, setSelectedLocationId] = useState<string | null>(null);
  const { state: scanState, connect: connectScanEvents, disconnect: disconnectScanEvents, showPanel: showScanPanel, setShowPanel: setShowScanPanel } = useScan();
  const utils = trpc.useUtils();

  const { setMarkerRef, getMarker } = useClusteredMarkers(filters.enableClustering);
  const collectionMode = useCollectionMode();
  const { tooltipLocationId, anchorElement, onMarkerMouseEnter, onMarkerMouseLeave, closeTooltip } =
    useMapTooltip({ getMarker });
  const [tooltipCollectionLocationId, setTooltipCollectionLocationId] = useState<string | null>(null);

  // Court placement mode
  const spotMode = useCourtPlacementMode();

  // Pin edit mode
  const pinEditMode = usePinEditMode();
  const updateCoordinatesMutation = trpc.locations.updateCoordinates.useMutation({
    onSuccess: async () => {
      // Await both refetches so the cache has new coordinates
      // before we clear the position override.
      await Promise.all([
        utils.locations.getById.invalidate({ id: selectedLocationId! }),
        utils.locations.getMapData.invalidate(),
      ]);
      pinEditMode.reset();
    },
  });

  // Zoom tracking for court pin visibility
  const [currentZoom, setCurrentZoom] = useState<number>(DEFAULT_ZOOM);
  useEffect(() => {
    if (!map) return;
    const listener = map.addListener("zoom_changed", () => {
      setCurrentZoom(map.getZoom() ?? DEFAULT_ZOOM);
    });
    return () => { listener.remove(); };
  }, [map]);

  const showCourtPinsOnMap = currentZoom >= 16 && filters.showCourtPins;

  // Court placement queries & mutations
  const { data: spotModeSpots } = trpc.sportCourts.listByLocation.useQuery(
    { locationId: spotMode.locationId! },
    { enabled: spotMode.active && !!spotMode.locationId },
  );
  const realCollectionIds = filters.collectionIds.filter((id) => id !== "__uncollected__");
  const { data: allCourtPins } = trpc.sportCourts.getMapData.useQuery(
    { collectionId: realCollectionIds[0] },
    { enabled: !spotMode.active && showCourtPinsOnMap && realCollectionIds.length === 1 },
  );
  const createSpotMutation = trpc.sportCourts.create.useMutation({
    onSuccess: () => {
      utils.sportCourts.listByLocation.invalidate({ locationId: spotMode.locationId! });
      utils.sportCourts.getMapData.invalidate();
    },
  });
  const updateSpotMutation = trpc.sportCourts.update.useMutation({
    onSuccess: () => {
      utils.sportCourts.listByLocation.invalidate({ locationId: spotMode.locationId! });
      utils.sportCourts.getMapData.invalidate();
    },
  });

  // Data hook
  const { markerData, culledMarkerData, mapData } = useMapData(filters, viewportBounds);

  // Defer marker rendering so pan/zoom stays responsive
  const deferredMarkers = useDeferredValue(culledMarkerData);

  // Wire up global search context
  const { registerMapHandler, clearMapHandler, setMarkerData: setSearchMarkerData, setMapBounds: setSearchMapBounds } = useSearchContext();

  // Sync marker data & bounds to context refs (no state change, no re-render)
  setSearchMarkerData(markerData);
  setSearchMapBounds(map?.getBounds() ?? null);

  // Handle deep-link from ?location= search param
  const hasHandledDeepLink = useRef(false);
  useEffect(() => {
    if (hasHandledDeepLink.current || !search.location) return;
    hasHandledDeepLink.current = true;
    setSelectedLocationId(search.location);
    navigate({ to: "/dashboard", search: (prev: Record<string, unknown>) => ({ ...prev, location: undefined }), replace: true });
  }, [search.location, navigate]);

  // Close tooltip on map click or drag (and handle spot placement clicks)
  useEffect(() => {
    if (!map) return;

    const clickListener = map.addListener("click", (e: google.maps.MapMouseEvent) => {
      closeTooltip();

      if (spotMode.active && spotMode.locationId && spotMode.armedSportType && e.latLng) {
        createSpotMutation.mutate({
          locationId: spotMode.locationId,
          sportType: spotMode.armedSportType,
          lat: e.latLng.lat(),
          lng: e.latLng.lng(),
        });
      }
    });
    const dragListener = map.addListener("dragstart", closeTooltip);
    return () => {
      clickListener.remove();
      dragListener.remove();
    };
  }, [map, closeTooltip, spotMode.active, spotMode.locationId, spotMode.armedSportType, spotModeSpots?.length, createSpotMutation]);

  const handleScanStarted = useCallback((scanId: string) => {
    connectScanEvents(scanId);
    setShowScanPanel(true);
  }, [connectScanEvents]);

  useEffect(() => {
    if (scanState.isComplete) {
      utils.locations.getMapData.invalidate();
      utils.scans.list.invalidate();
    }
  }, [scanState.isComplete, utils]);

  const handlePanelLocationClick = useCallback(
    (lat: number, lng: number) => {
      if (map) {
        map.panTo({ lat, lng });
        map.setZoom(15);
      }
    },
    [map]
  );

  // Auto-pan to fit all markers on initial load (once)
  const hasFittedBounds = useRef(false);
  useEffect(() => {
    if (!map || markerData.length === 0 || hasFittedBounds.current) return;
    const bounds = new google.maps.LatLngBounds();
    for (const loc of markerData) {
      bounds.extend({ lat: loc.lat, lng: loc.lng });
    }
    map.fitBounds(bounds, 50);
    hasFittedBounds.current = true;
  }, [map, markerData]);

  // Zoom to fit markers when collection filter changes
  const prevCollectionKey = useRef<string>("");
  useEffect(() => {
    const key = filters.collectionIds.join(",");
    if (!map || key === prevCollectionKey.current) return;
    prevCollectionKey.current = key;
    if (filters.collectionIds.length === 0 || markerData.length === 0) return;

    const bounds = new google.maps.LatLngBounds();
    for (const loc of markerData) {
      bounds.extend({ lat: loc.lat, lng: loc.lng });
    }
    map.fitBounds(bounds, 50);
  }, [map, filters.collectionIds, markerData]);

  // O(1) lookup map for tooltip data (use full markerData, not culled)
  const markerDataMap = useMemo(() => {
    const map = new Map<string, (typeof markerData)[number]>();
    for (const loc of markerData) {
      map.set(loc.id, loc);
    }
    return map;
  }, [markerData]);

  // Wire search context — use a ref so the handler always reads latest values
  // without re-registering (which would cause an infinite re-render loop).
  const searchHandlerRef = useRef<((id: string) => void) | undefined>(undefined);
  searchHandlerRef.current = (idOrPan: string) => {
    if (idOrPan.startsWith("__pan:")) {
      const [, lat, lng] = idOrPan.split(":");
      if (map) {
        map.panTo({ lat: Number(lat), lng: Number(lng) });
        map.setZoom(18);
      }
      return;
    }
    closeTooltip();
    setSelectedLocationId(idOrPan);
    const loc = markerDataMap.get(idOrPan);
    if (map && loc) {
      map.panTo({ lat: loc.lat, lng: loc.lng });
      map.setZoom(18);
      map.panBy(getSidebarPanOffset(), 0);
    }
  };

  useEffect(() => {
    registerMapHandler((id: string) => searchHandlerRef.current?.(id));
    return () => clearMapHandler();
  }, [registerMapHandler, clearMapHandler]);

  // Stable callbacks for marker clicks
  const handleScannedMarkerClick = useCallback((id: string) => {
    closeTooltip();
    if (collectionMode.state.active) {
      collectionMode.toggle(id);
      return;
    }
    if (spotMode.active && spotMode.locationId !== id) {
      spotMode.exit();
    }
    // Cancel pin edit when switching locations
    if (pinEditMode.active && pinEditMode.locationId !== id) {
      pinEditMode.cancel();
    }
    setSelectedLocationId(id);
  }, [collectionMode, closeTooltip, spotMode, pinEditMode, map, markerDataMap]);

  const handleLiveScanMarkerClick = useCallback((id: string) => {
    const loc = scanState.discoveredLocations.get(id);
    if (map && loc) {
      map.panTo({ lat: loc.lat, lng: loc.lng });
      map.setZoom(15);
    }
  }, [map, scanState.discoveredLocations]);

  const handleTooltipAddToCollection = useCallback((locationId: string) => {
    closeTooltip();
    setTooltipCollectionLocationId(locationId);
  }, [closeTooltip]);

  const handleLocationClick = useCallback(
    (_locationId: string, lat: number, lng: number) => {
      if (spotMode.active) spotMode.exit();
      setSelectedLocationId(_locationId);
      if (map) {
        map.panTo({ lat, lng });
        map.setZoom(15);
        map.panBy(getSidebarPanOffset(), 0);
      }
    },
    [map, spotMode]
  );

  const handleViewDetails = useCallback((locationId: string) => {
    closeTooltip();
    if (spotMode.active) spotMode.exit();
    setSelectedLocationId(locationId);
  }, [closeTooltip, spotMode]);

  // Spot placement handlers
  const handleEnterSpotPlacement = useCallback((locationId: string) => {
    // Mutual exclusivity: cancel pin edit when entering spot mode
    if (pinEditMode.active) pinEditMode.cancel();
    const loc = markerDataMap.get(locationId);
    const center = loc ? { lat: loc.lat, lng: loc.lng } : { lat: 52.1326, lng: 5.2913 };
    spotMode.enter(locationId, center, getSidebarPanOffset() * 2);
  }, [markerDataMap, spotMode, pinEditMode]);

  const handleExitSpotPlacement = useCallback(() => {
    spotMode.exit();
  }, [spotMode]);

  const handleZoomToLocation = useCallback((locationId: string) => {
    const loc = markerDataMap.get(locationId);
    if (map && loc) {
      map.panTo({ lat: loc.lat, lng: loc.lng });
      map.setZoom(18);
      map.setMapTypeId("hybrid");
      map.panBy(getSidebarPanOffset(), 0);
    }
  }, [map, markerDataMap]);

  // Handle spot drag end
  const handleSpotDragEnd = useCallback((spotId: string) => (lat: number, lng: number) => {
    updateSpotMutation.mutate({ id: spotId, lat, lng });
  }, [updateSpotMutation]);

  // Pin edit handlers
  const handleEnterPinEdit = useCallback(() => {
    if (!selectedLocationId) return;
    const loc = markerDataMap.get(selectedLocationId);
    if (!loc) return;
    // Exit spot mode if active (mutual exclusivity)
    if (spotMode.active) spotMode.exit();
    pinEditMode.enter(selectedLocationId, { lat: loc.lat, lng: loc.lng });
  }, [selectedLocationId, markerDataMap, spotMode, pinEditMode]);

  const handlePinDragEnd = useCallback((lat: number, lng: number) => {
    pinEditMode.onDrag(lat, lng);
  }, [pinEditMode]);

  const handleSavePinEdit = useCallback(() => {
    const coords = pinEditMode.save();
    if (coords && selectedLocationId) {
      updateCoordinatesMutation.mutate({ locationId: selectedLocationId, ...coords });
    }
  }, [pinEditMode, selectedLocationId, updateCoordinatesMutation]);

  const handleCancelPinEdit = useCallback(() => {
    pinEditMode.cancel();
  }, [pinEditMode]);

  // Find selected location data for spot mode marker rendering
  const selectedMarkerData = selectedLocationId ? markerDataMap.get(selectedLocationId) : null;

  const isSidebarOpen = selectedLocationId !== null;

  return (
    <>
      {/* Main scanned markers — hidden during spot placement mode, using culled data */}
      {!spotMode.active && deferredMarkers.map((loc) => {
        const isPinEditing = pinEditMode.active && loc.id === selectedLocationId;
        return (
          <MapMarker
            key={loc.id}
            locationId={loc.id}
            position={
              isPinEditing && pinEditMode.draggedPosition
                ? pinEditMode.draggedPosition
                : loc.position
            }
            hasSportField={loc.hasSportField}
            primaryType={loc.primary_type}
            name={loc.name}
            ownership={loc.ownership}
            isMuted={loc.isMuted}
            isDimmed={showCourtPinsOnMap}
            source={loc.source}
            isReviewed={loc.isReviewed}
            isRejected={loc.isRejected}
            approvedByCity={loc.approvedByCity}
            approvedBySponsor={loc.approvedBySponsor}
            isSelected={loc.id === selectedLocationId}
            isInCollection={collectionMode.state.active && collectionMode.isInCollection(loc.id)}
            collectionColor={collectionMode.state.active ? collectionMode.state.collectionColor ?? undefined : undefined}
            isDraggable={isPinEditing}
            onDragEnd={isPinEditing ? handlePinDragEnd : undefined}
            onClick={handleScannedMarkerClick}
            onMouseEnter={onMarkerMouseEnter}
            onMouseLeave={onMarkerMouseLeave}
            setMarkerRef={setMarkerRef}
          />
        );
      })}

      {/* Live-discovered markers from active scan */}
      {!spotMode.active && scanState.isConnected && Array.from(scanState.discoveredLocations.values()).map((loc) => {
        const alreadyInMapData = mapData?.some((m: any) => m.place_id === loc.placeId);
        if (alreadyInMapData) return null;
        return (
          <MapMarker
            key={`live-${loc.placeId}`}
            locationId={loc.placeId}
            position={{ lat: loc.lat, lng: loc.lng }}
            hasSportField={loc.hasSportField}
            primaryType={loc.primaryType}
            sportTypes={loc.sportTypes.length > 0 ? loc.sportTypes : null}
            name={loc.name}
            ownership={loc.ownership ?? undefined}
            isProcessing={loc.processingStage === "discovered"}
            onClick={handleLiveScanMarkerClick}
          />
        );
      })}

      {/* Spot placement mode: show only the selected location marker + its draggable court pins */}
      {spotMode.active && selectedMarkerData && (
        <>
          <MapMarker
            key={`spot-mode-${selectedMarkerData.id}`}
            locationId={selectedMarkerData.id}
            position={{ lat: selectedMarkerData.lat, lng: selectedMarkerData.lng }}
            hasSportField={selectedMarkerData.hasSportField}
            primaryType={selectedMarkerData.primary_type}
            name={selectedMarkerData.name}
            ownership={selectedMarkerData.ownership}
            isMuted={selectedMarkerData.isMuted}
            isReviewed={selectedMarkerData.isReviewed}
            isRejected={selectedMarkerData.isRejected}
            isSelected
            onClick={handleScannedMarkerClick}
          />
          {spotModeSpots?.map((court: any) => (
            <SportCourtMarker
              key={court.id}
              courtId={court.id}
              position={{ lat: court.lat, lng: court.lng }}
              sportType={court.sport_type}
              isDraggable
              onDragEnd={handleSpotDragEnd(court.id)}
            />
          ))}
        </>
      )}

      {/* Normal mode court pins — visible at zoom >= 16 with filter on */}
      {!spotMode.active && showCourtPinsOnMap && allCourtPins?.map((court: any) => (
        <SportCourtMarker
          key={court.id}
          courtId={court.id}
          position={{ lat: court.lat, lng: court.lng }}
          sportType={court.sport_type}
          onClick={(courtId) => {
            // Select the parent location
            const locationId = court.location_id;
            if (locationId) {
              setSelectedLocationId(locationId);
            }
          }}
        />
      ))}

      {/* Scan circle overlay */}
      <ScanCircleOverlay
        activeCells={scanState.activeCells}
        isScanning={scanState.currentPhase === "scanning" && !scanState.isComplete}
      />

      {/* PolygonScanTool */}
      {isLocalDev && !spotMode.active && (
        <PolygonScanTool
          onScanStarted={handleScanStarted}
          isScanActive={scanState.isConnected && !scanState.isComplete}
          isSidebarOpen={isSidebarOpen}
        />
      )}

      {/* Map controls — hidden during spot mode */}
      {!spotMode.active && (
        <div
          className="absolute top-3 z-10 flex flex-col gap-2 transition-[right] duration-300"
          style={{ right: isSidebarOpen ? 'calc(560px + 0.75rem)' : '0.75rem' }}
        >
          <MapFilters filters={filters} onFiltersChange={setFilters} />
          <CollectionModePanel
            active={collectionMode.state.active}
            collectionName={collectionMode.state.collectionName}
            collectionColor={collectionMode.state.collectionColor}
            count={collectionMode.locationIds.size}
            onActivate={(col) => collectionMode.enter(col)}
            onDeactivate={() => collectionMode.exit()}
          />
        </div>
      )}

      {/* Court placement mode toolbar */}
      {spotMode.active && (
        <div className="absolute top-3 left-0 right-0 pr-[560px] lg:pr-[440px] xl:pr-[480px] 2xl:pr-[560px] z-10 flex justify-center pointer-events-none">
          <div className="bg-background/95 backdrop-blur-sm border px-4 py-2.5 rounded-lg shadow-lg pointer-events-auto flex flex-col items-center gap-2">
            <span className="text-xs font-medium text-muted-foreground">
              {spotMode.armedSportType ? "Click map to place court" : "Select a sport type below"}
            </span>
            <CourtPlacementToolbar
              armedSportType={spotMode.armedSportType}
              onArm={spotMode.arm}
              onDisarm={spotMode.disarm}
            />
          </div>
        </div>
      )}

      <MapLegend />

      {/* Empty state: no collection selected */}
      {filters.collectionIds.length === 0 && !spotMode.active && (
        <div className="absolute inset-0 z-[5] flex items-center justify-center pointer-events-none">
          <div className="bg-background/90 backdrop-blur-sm border rounded-lg px-6 py-4 shadow-lg text-center pointer-events-auto max-w-sm">
            <p className="text-sm font-medium text-foreground">Select a collection to view locations</p>
            <p className="text-xs text-muted-foreground mt-1">Use the Collection filter in the top-right to get started</p>
          </div>
        </div>
      )}

      <LocationTableDrawer
        locations={culledMarkerData}
        onLocationClick={handleLocationClick}
        isSidebarOpen={isSidebarOpen}
      />

      <LocationSidebar
        locationId={selectedLocationId}
        onClose={() => {
          if (pinEditMode.active) pinEditMode.cancel();
          setSelectedLocationId(null);
        }}
        onEnterSpotPlacement={handleEnterSpotPlacement}
        onExitSpotPlacement={handleExitSpotPlacement}
        isSpotPlacementActive={spotMode.active}
        onZoomToLocation={handleZoomToLocation}
        isPinEditActive={pinEditMode.active}
        hasPinMoved={pinEditMode.hasMoved}
        isSavingPin={updateCoordinatesMutation.isPending}
        onEnterPinEdit={handleEnterPinEdit}
        onSavePinEdit={handleSavePinEdit}
        onCancelPinEdit={handleCancelPinEdit}
      />

      {isLocalDev && showScanPanel && (
        <ScanProgressPanel
          state={scanState}
          onClose={() => {
            setShowScanPanel(false);
            if (scanState.isComplete) {
              disconnectScanEvents();
            }
          }}
          onLocationClick={handlePanelLocationClick}
        />
      )}

      {/* Hover tooltip — disabled during spot mode */}
      {!spotMode.active && tooltipLocationId && anchorElement && markerDataMap.has(tooltipLocationId) && (
        <MapTooltip
          locationId={tooltipLocationId}
          anchor={anchorElement}
          markerData={markerDataMap.get(tooltipLocationId)!}
          onClose={closeTooltip}
          onAddToCollection={handleTooltipAddToCollection}
          onViewDetails={handleViewDetails}
        />
      )}

      {/* Add to collection dialog (from tooltip) */}
      <AddToCollectionDialog
        open={tooltipCollectionLocationId !== null}
        onOpenChange={(open) => { if (!open) setTooltipCollectionLocationId(null); }}
        locationIds={tooltipCollectionLocationId ? [tooltipCollectionLocationId] : []}
      />
    </>
  );
}

export function MapPage() {
  const { data: prefs } = trpc.settings.get.useQuery(undefined, {
    staleTime: 60_000,
  });

  const center = prefs?.defaultMapCenter ?? NETHERLANDS_CENTER;
  const zoom = prefs?.defaultMapZoom ?? DEFAULT_ZOOM;

  return (
    <div className="relative h-full w-full">
      <ErrorBoundary name="GoogleMap">
        <GoogleMapWrapper
          defaultCenter={center}
          defaultZoom={zoom}
          className="h-full w-full"
        >
          <MapContent />
        </GoogleMapWrapper>
      </ErrorBoundary>
    </div>
  );
}
